import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import {
  getCustomer, getConnections, getConnectorVendors, getPlatformConfig,
  testConnection, saveConnection, deleteConnection, syncConnector, getSyncStatus, getActiveJob,
  getConnectorTables, deleteTable, resolveEndpointParams, getEndpointCatalog
} from '../../api'
import './Connectors.css'
import { connectionBadge, connectorNote } from '../../utils/providedThrough'
import { groupConnections, saveFailure } from '../../utils/connectionGroups'
import { listFrom } from '../../utils/loadState'
import { connectionHealth } from '../../utils/connectionHealth'
import { API_BASE as API_BASE_CONFIG } from '../../config'

// THE BACKEND BASE URL, AT MODULE SCOPE.
// The endpoint-catalog fetch used a BARE RELATIVE PATH, so in production it hit the
// Vercel frontend domain instead of Railway and got index.html back — hence
// `SyntaxError: Unexpected token '<', "<!DOCTYPE"`. That fetch has NEVER worked in
// production. The old `.catch()` silently substituted the hardcoded list, so the page
// always looked fine and every endpoint list ever seen here was the hand-maintained
// one. The "dynamic catalog" was decorative. Removing the silent fallback surfaced it
// within minutes of deploy (Scott, 2026-08-11), which is the entire argument for
// failing loudly. An identical constant was defined LOCALLY inside another component,
// which is why every other call in this file works.
const API = API_BASE_CONFIG

const CRED_FIELDS = {
  REST: [
    { key: 'username', label: 'Service Account / Username', placeholder: 'e.g. SERVICEACCT01', type: 'text',
      hint: 'UKG Admin → System Configuration → Security → Service Accounts' },
    { key: 'password', label: 'Password', placeholder: '', type: 'password' },
    { key: 'api_key', label: 'User API Key', placeholder: 'e.g. XXXXXXXXXXXX', type: 'password',
      hint: 'On the Service Account page, listed to the far right of the Username under User API Key' },
    { key: 'customer_api_key', label: 'Customer API Key', placeholder: 'e.g. XXXXX', type: 'password',
      hint: 'UKG Admin → System Configuration → Security → Web Services → Customer API Key' },
    { key: 'base_url', label: 'Base URL', placeholder: 'e.g. https://service4.ultipro.com', type: 'text',
      hint: 'Your UKG tenant hostname — check the browser URL when logged into UKG Pro' },
    { key: 'dev_hub_username', label: 'Developer Hub Username', placeholder: '', type: 'text', optional: true,
      hint: 'UKG Developer Hub / Community login — separate from the credentials above, used only to pull API spec documentation. Shared across Pro, WFM, and Ready — only needs to be entered once, on any one connection.' },
    { key: 'dev_hub_password', label: 'Developer Hub Password', placeholder: '', type: 'password', optional: true },
  ],
  SOAP: [
    { key: 'username', label: 'Service Account / Username', placeholder: 'e.g. SERVICEACCT01', type: 'text',
      hint: 'UKG Admin → System Configuration → Security → Service Accounts' },
    { key: 'password', label: 'Password', placeholder: '', type: 'password' },
    { key: 'api_key', label: 'Customer API Key (ClientAccessKey)', placeholder: 'e.g. XXXXX', type: 'password',
      hint: 'UKG Admin → System Configuration → Security → Web Services → Customer API Key' },
    { key: 'customer_api_key', label: 'User API Key (UserAccessKey)', placeholder: 'e.g. XXXXXXXXXXXX', type: 'password',
      hint: 'On the Service Account page, listed to the far right of the Username under User API Key' },
    { key: 'base_url', label: 'Base URL', placeholder: 'e.g. https://service4.ultipro.com', type: 'text',
      hint: 'Your UKG tenant hostname — check the browser URL when logged into UKG Pro' },
  ],
  RAAS: [
    { key: 'username', label: 'Service Account / Username', placeholder: 'e.g. SERVICEACCT01', type: 'text',
      hint: 'UKG Admin → System Configuration → Security → Service Accounts' },
    { key: 'password', label: 'Password', placeholder: '', type: 'password' },
    { key: 'customer_api_key', label: 'Customer API Key (ClientAccessKey)', placeholder: 'e.g. XXXXX', type: 'password',
      hint: 'UKG Admin → System Configuration → Security → Web Services → Customer API Key' },
    { key: 'api_key', label: 'User API Key (UserAccessKey)', placeholder: 'e.g. XXXXXXXXXXXX', type: 'password',
      hint: 'On the Service Account page, listed to the far right of the Username under User API Key. Note: this may differ from the REST User API Key' },
    { key: 'base_url', label: 'Base URL', placeholder: 'e.g. https://service4.ultipro.com', type: 'text',
      hint: 'Your UKG tenant hostname — same as REST/SOAP base URL' },
  ],
  GraphQL: [
    { key: 'username', label: 'Service Account / Username', placeholder: '', type: 'text' },
    { key: 'password', label: 'Password', placeholder: '', type: 'password' },
    { key: 'api_key', label: 'API Key', placeholder: '', type: 'password' },
    { key: 'base_url', label: 'GraphQL Endpoint URL', placeholder: 'e.g. https://service5.ultipro.com/graphql', type: 'text' },
  ],
  OAuth: [
    { key: 'client_id', label: 'Client ID', placeholder: '', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', placeholder: '', type: 'password' },
    { key: 'tenant_id', label: 'Tenant ID', placeholder: '', type: 'text' },
    { key: 'base_url', label: 'Authorization URL', placeholder: '', type: 'text' },
  ],
  WFM: [
    { key: 'hostname', label: 'Tenant Hostname', placeholder: 'e.g. acme.npr.mykronos.com', type: 'text' },
    { key: 'username', label: 'Integration Username', placeholder: 'e.g. Integration1', type: 'text' },
    { key: 'password', label: 'Integration Password', placeholder: '', type: 'password' },
    { key: 'client_id', label: 'Client ID', placeholder: 'From WFM Client Management screen', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', placeholder: 'From WFM Client Management screen', type: 'password' },
    { key: 'tenant_id', label: 'Realm', placeholder: 'From WFM Client Management screen (e.g. xxxxx-custom)', type: 'text' },
    { key: 'dev_hub_username', label: 'Developer Hub Username', placeholder: '', type: 'text', optional: true,
      hint: 'UKG Developer Hub / Community login — separate from the credentials above, used only to pull API spec documentation. Shared across Pro, WFM, and Ready — only needs to be entered once, on any one connection.' },
    { key: 'dev_hub_password', label: 'Developer Hub Password', placeholder: '', type: 'password', optional: true },
  ],
  // UKG Pro Recruiting / Onboarding: an identity client UKG support issues per tenant.
  // The tenant alias is read from the token, so there is no alias or URL to enter.
  'UKG Identity': [
    { key: 'client_id', label: 'Client ID', placeholder: '', type: 'text',
      hint: 'From the UKG support case that issued the client' },
    { key: 'client_secret', label: 'Client Secret (Secret Key)', placeholder: '', type: 'password' },
    { key: 'tenant_id', label: 'Organization ID', placeholder: '', type: 'text',
      hint: 'Sent by UKG with the Client ID and Secret Key' },
  ],
  Token: [
    { key: 'access_token', label: 'Access Token', placeholder: '', type: 'password' },
    { key: 'base_url', label: 'Base URL', placeholder: '', type: 'text' },
  ],
  READY: [
    { key: 'hostname', label: 'Hostname', placeholder: 'e.g. secure60.saashr.com', type: 'text',
      hint: 'Domain from your Ready login URL — e.g. secure60.saashr.com' },
    { key: 'company_short_name', label: 'Company Short Name / ID', placeholder: 'e.g. 1234567', type: 'text',
      hint: 'Settings → Global Setup → Company Setup → Company Info → scroll to Company Address → Company Short Name' },
    { key: 'username', label: 'Service Account Username', placeholder: 'e.g. SERVICEACCT01', type: 'text',
      hint: 'Settings → Global Setup → Company Setup → Login Config → Service Accounts' },
    { key: 'password', label: 'Password', placeholder: '', type: 'password' },
    { key: 'api_key', label: 'API Key', placeholder: '', type: 'password',
      hint: 'Settings → Global Setup → Company Setup → Login Config → API Keys' },
    { key: 'dev_hub_username', label: 'Developer Hub Username', placeholder: '', type: 'text', optional: true,
      hint: 'UKG Developer Hub / Community login — separate from the credentials above, used only to pull API spec documentation. Shared across Pro, WFM, and Ready — only needs to be entered once, on any one connection.' },
    { key: 'dev_hub_password', label: 'Developer Hub Password', placeholder: '', type: 'password', optional: true },
  ],
}

function getApiTypeFromProduct(productId) {
  if (!productId) return 'REST'
  const p = productId.toUpperCase()
  if (p.includes('RAAS')) return 'RAAS'
  if (p.includes('SOAP')) return 'SOAP'
  if (p.includes('GRAPHQL')) return 'GraphQL'
  if (p.includes('RECRUITING') || p.includes('ONBOARDING')) return 'UKG Identity'
  if (p.includes('TALENT')) return 'OAuth'
  if (p.includes('WFM')) return 'WFM'
  if (p.includes('READY')) return 'READY'
  return 'REST'
}

/* ─── Sync Endpoint Defaults ─── */
// ─────────────────────────────────────────────────────────────────────────────
// THE HARDCODED ENDPOINT CATALOG LIVED HERE. IT IS GONE — #120.
// ─────────────────────────────────────────────────────────────────────────────
// 452 endpoints were maintained BY HAND in this file: REST_ENDPOINTS (206),
// TALENT_ENDPOINTS (92), WFM_ENDPOINTS (81), SOAP_SERVICES (51),
// READY_ENDPOINTS (12). A second catalog, drifting from the schema BOTH ways.
//
// Missing from it, but real and ingested: wfm_paycode_totals_daily (millions
// of rows at a WFM tenant — the table the dark WFM metrics needed), wfm_person_sets,
// wfm_ia_accrual_transactions. Present in it but in NO schema: 51 names
// including employee_supervisor_details, pay_detail_reports, general_ledger,
// pto, termination and every ready_*. So this page could offer what the backend
// cannot fetch, and could not offer what the backend has.
//
// Scott, 2026-08-11: 'I have NO WAY to load them because the front end endpoints
// were never updated to reflect everything we found. I have no idea how we don't
// wind up in this exact same situation if we're not ticking and typing from back
// end through front end.'
//
// The list now comes from GET /api/connector/endpoints/{vendor}/{product}, which
// derives it from config/schemas/**. Gate TABLE-OFFERABLE ties metric demand to
// that same supply, so a metric written against a table nobody can tick fails on
// the commit that adds it. DO NOT REINTRODUCE A LOCAL LIST: a second catalog
// cannot be kept honest, and a stale one is worse than none because you tick
// something that cannot sync and conclude the platform is broken.

const ENDPOINT_PARAMS = {
  pay_register: [
    { name: 'companyId', label: 'Company', type: 'select', required: false },
    { name: 'payRunId', label: 'Pay Run', type: 'select', required: false },
  ],
  company_pay_statements: [
    { name: 'companyId', label: 'Company', type: 'select', required: true },
    { name: 'payGroupCode', label: 'Pay Group', type: 'select', required: false },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
  ],
  company_pay_summary: [
    { name: 'companyId', label: 'Company', type: 'select', required: true },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
  ],
  pay_details_report: [
    { name: 'companyId', label: 'Company', type: 'select', required: true },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
  ],
  pay_statements: [
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
  ],
  earnings_history: [
    { name: 'companyId', label: 'Company', type: 'select', required: false },
  ],
  payroll_deductions_history: [
    { name: 'companyId', label: 'Company', type: 'select', required: false },
  ],
  general_ledger_runs: [
    { name: 'companyId', label: 'Company', type: 'select', required: false },
  ],
  employee_changes_by_date: [
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
  ],
  third_party_pay: [
    { name: 'companyId', label: 'Company', type: 'select', required: false },
  ],
}


/* ─── ConnectionBlock: one saved connection with its own sync ─── */
function ConnectionBlock({ connection, system, customerId, onSaved, onDelete }) {
  const [syncing, setSyncing] = useState(null)
  const [syncStatus, setSyncStatus] = useState(null)
  const [showSyncSettings, setShowSyncSettings] = useState(false)
  const [reconfiguring, setReconfiguring] = useState(false)
  const [credValues, setCredValues] = useState({})
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [endpointCatalog, setEndpointCatalog] = useState(null)   // dynamic from API
  const [catalogError, setCatalogError] = useState(null)         // never fall back silently
  const [selectedEndpoints, setSelectedEndpoints] = useState({})
  const [endpointParams, setEndpointParams] = useState({})   // { endpoint_key: { paramName: value } }
  const [expandedParam, setExpandedParam] = useState(null)    // which endpoint has params open
  const [paramOptions, setParamOptions] = useState({})         // { endpoint_key: { paramName: { options, loading } } }
  const [loadingParams, setLoadingParams] = useState(null)
  // ITEM 138, 2026-09-24: this used to be TWO independent local `const poll`
  // intervals -- one in the mount-recovery effect below, one in handleSync --
  // neither aware of the other, with nothing shared to detect or clear a
  // duplicate. If the mount check found this job "running" while (or after)
  // handleSync had already started its own poll for it, two intervals wrote
  // syncStatus/syncing on whatever cadence they each happened to land on,
  // with no ordering guarantee -- the same "uncoordinated writer" shape as
  // AdminDevHubCapture.jsx's probe-run race (items 124/129/130/132/136),
  // just via duplicate intervals instead of a stale single read. One shared
  // ref + one shared watch function, same proven fix.
  const syncPollRef = useRef(null)
  useEffect(() => () => { if (syncPollRef.current) clearInterval(syncPollRef.current) }, [])

  // Only watches an ALREADY-RUNNING sync job -- called from the mount-recovery
  // effect below and handleSync. Clearing any existing interval before
  // starting a new one (never refusing outright) means at most one thing is
  // ever watching this connection's sync, no matter which caller triggers it.
  function watchSync(jobId) {
    if (syncPollRef.current) clearInterval(syncPollRef.current)
    syncPollRef.current = setInterval(async () => {
      try {
        const status = await getSyncStatus(jobId)
        setSyncStatus(status)
        if (['completed', 'completed_with_errors', 'complete', 'error', 'failed', 'interrupted'].includes(status.status)) {
          clearInterval(syncPollRef.current)
          syncPollRef.current = null
          setSyncing(status.status === 'completed' || status.status === 'complete' ? 'done' : 'error')
          if (onSaved) onSaved()
        }
      } catch {
        clearInterval(syncPollRef.current)
        syncPollRef.current = null
        setSyncing(null)
      }
    }, 3000)
  }

  // Resume status bar if a sync job is active for this connection
  useEffect(() => {
    if (!connection?.product_id || !customerId) return
    let cancelled = false
    async function checkActiveJob() {
      try {
        const active = await getActiveJob(customerId, connection.product_id)
        if (cancelled || !active?.found) return
        if (active.status === 'running') {
          setSyncing('running')
          setSyncStatus(active)
          watchSync(active.job_id)
        }
      } catch { /* no active job */ }
    }
    checkActiveJob()
    return () => { cancelled = true }
  }, [customerId, connection?.product_id])


  // Fetch endpoint catalog dynamically from backend schema
  useEffect(() => {
    if (!connection.product_id) return
    const vendorId = (system.vendor_id || 'UKG').toUpperCase()
    const productId = connection.product_id
    // USE THE SHARED HELPER. This was a hand-rolled fetch with a BARE RELATIVE PATH,
    // so in production it hit the Vercel frontend domain instead of Railway and got
    // index.html back — `SyntaxError: Unexpected token '<', "<!DOCTYPE"`. It had NEVER
    // worked in production; the old .catch() silently substituted the hardcoded list,
    // so the page always looked fine and every endpoint list ever seen here was the
    // hand-maintained one. Removing the silent fallback surfaced it within minutes of
    // deploy, which is the whole argument for failing loudly.
    //
    // api.js `request()` already handles the base URL, auth, 401 redirect and MFA. The
    // hand-rolled version reimplemented a worse copy of all four.
    getEndpointCatalog(vendorId, productId, customerId)
      .then(data => {
        // Convert array to object keyed by name, preserving default_on as default
        const catalog = {}
        data.forEach(ep => {
          catalog[ep.name] = {
            label: ep.description || ep.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            area: ep.functional_area ? ep.functional_area.charAt(0).toUpperCase() + ep.functional_area.slice(1).replace(/_/g, ' ') : 'Other',
            default: ep.default_on !== false,
            heavy: ep.heavy || false,
            path: ep.path,
            // WHY THIS BOX IS UNCHECKED. The backend has always known — every
            // off-by-default endpoint carries a tagged reason, enforced by gate W2.5 —
            // and the API dropped the field until 2026-08-11. An unchecked box with no
            // explanation reads as "we found nothing", which is how a platform with
            // 4M rows of data looked empty.
            disabled_reason: ep.disabled_reason || '',
            table_name: ep.table_name || null,
            last_synced_at: ep.last_synced_at || null,
            row_count: ep.row_count != null ? ep.row_count : null,
          }
        })
        setEndpointCatalog(catalog)
        // Set default selection based on default_on
        setSelectedEndpoints(
          Object.fromEntries(Object.entries(catalog).map(([k, v]) => [k, v.default]))
        )
      })
      .catch(err => {
        // NO SILENT FALLBACK. This used to swap in a hand-maintained list of 452
        // endpoints on any failure, with a console.warn nobody sees — so the screen
        // could not tell you which catalog you were looking at. That list has drifted
        // BOTH ways: it is missing wfm_paycode_totals_daily (millions of rows at a WFM tenant) and
        // wfm_person_sets, and it offers 51 names no schema can fetch. Showing it as
        // if it were real is worse than showing nothing, because you tick something
        // that cannot sync and conclude the platform is broken.
        console.error('Endpoint catalog fetch failed:', err)
        setEndpointCatalog({})
        setSelectedEndpoints({})
        setCatalogError(String(err))
      })
  }, [connection.product_id, customerId])

  // Toggle param panel for an endpoint; resolve dropdown values on first open
  async function toggleParams(epKey) {
    if (expandedParam === epKey) { setExpandedParam(null); return }
    setExpandedParam(epKey)
    // Only resolve if we haven't yet
    if (!paramOptions[epKey]) {
      setLoadingParams(epKey)
      try {
        const result = await resolveEndpointParams(customerId, epKey)
        setParamOptions(prev => ({ ...prev, [epKey]: result.resolved || {} }))
      } catch (e) {
        console.error('Param resolution failed:', e)
        setParamOptions(prev => ({ ...prev, [epKey]: {} }))
      }
      setLoadingParams(null)
    }
  }

  function setParamValue(epKey, paramName, value) {
    setEndpointParams(prev => ({
      ...prev,
      [epKey]: { ...(prev[epKey] || {}), [paramName]: value }
    }))
  }

  const apiType = getApiTypeFromProduct(connection.product_id)
  const isConnected = connection.test_status === 'success'

  async function handleDelete() {
    if (!confirm(`Delete ${apiType} connection for ${system.vendor_id}?`)) return
    try {
      await deleteConnection(customerId, system.vendor_id, connection.product_id)
      if (onDelete) onDelete()
    } catch (err) { alert('Delete failed: ' + err.message) }
  }

  async function handleRetest() {
    const fields = CRED_FIELDS[apiType] || CRED_FIELDS.REST
    const params = { vendor_id: system.vendor_id, product_id: connection.product_id }
    // Only include credentials the user actually entered
    fields.forEach(f => {
      if (credValues[f.key] && credValues[f.key].trim()) {
        params[f.key] = credValues[f.key]
      }
    })
    setTesting(true); setTestResult(null)
    try {
      const r = await testConnection(params, customerId)
      setTestResult(r)
    } catch (err) { setTestResult({ success: false, error: err.message }) }
    setTesting(false)
  }

  async function handleResave() {
    const fields = CRED_FIELDS[apiType] || CRED_FIELDS.REST
    const params = { vendor_id: system.vendor_id, product_id: connection.product_id }
    // Only include credentials the user actually changed (non-empty)
    let hasChanges = false
    fields.forEach(f => {
      if (credValues[f.key] && credValues[f.key].trim()) {
        params[f.key] = credValues[f.key]
        hasChanges = true
      }
    })
    // If no credentials changed, just save endpoint config
    if (!hasChanges) {
      setReconfiguring(false)
      setTestResult({ success: true, message: 'Configuration updated (credentials unchanged)' })
      if (onSaved) onSaved()
      return
    }
    setSaving(true)
    try {
      const r = await saveConnection(customerId, params)
      if (r.success) {
        setReconfiguring(false)
        setCredValues({})
        setTestResult(null)
        if (onSaved) onSaved()
      } else {
        // debug_fields_supplied_this_request (field NAMES only, never values) shows
        // exactly what the backend saw as non-empty in this request -- surfaced
        // because a save failure here has repeatedly not matched what the form
        // appeared to send.
        const fieldsNote = r.debug_fields_supplied_this_request
          ? `\n\nFields this request actually sent: ${r.debug_fields_supplied_this_request.join(', ') || '(none)'}`
          : ''
        alert((r.error || 'Save failed') + fieldsNote)
      }
    } catch (err) { alert('Save failed: ' + err.message) }
    setSaving(false)
  }

  async function handleSync() {
    const vendorId = (system.vendor_id || '').toUpperCase()
    const productId = connection.product_id
    if (!productId) return

    const endpointNames = Object.entries(selectedEndpoints)
      .filter(([, on]) => on)
      .map(([name]) => name)

    if (endpointNames.length === 0) { alert('Select at least one endpoint to sync'); return }

    setSyncing('starting'); setSyncStatus(null)
    try {
      const result = await syncConnector(customerId, vendorId, productId, endpointNames, endpointParams)
      const jobId = result.job_id
      if (!jobId) { setSyncing(null); return }
      setSyncing('running')
      watchSync(jobId)
    } catch (err) { alert('Sync failed: ' + err.message); setSyncing(null) }
  }

  // The static list is NOT a fallback here either — an empty catalog renders the
  // error below rather than a different, hand-maintained catalog wearing the same UI.
  const endpointDefs = endpointCatalog || {}
  const areas = [...new Set(Object.values(endpointDefs).map(e => e.area))].sort()
  const selectedCount = Object.values(selectedEndpoints).filter(Boolean).length
  const totalCount = Object.keys(endpointDefs).length

  return (
    <div className="cn-connection-block">
      <div className="cn-connection-block-header">
        <div className="cn-connection-block-type">
          <span className={`cn-api-badge cn-api-badge-${apiType.toLowerCase()}`}>{apiType}</span>
          <span className="cn-connection-block-product">{connection.product_id}</span>
        </div>
        <span className={`cn-status-badge ${isConnected ? 'cn-status-connected' : 'cn-status-error'}`}>
          {isConnected ? '✓ Connected' : connection.test_status || 'Unknown'}
        </span>
      </div>

      <div className="cn-connection-block-meta">{connectionHealth(connection).line}</div>
      {connection.last_tested && (
        <div className="cn-connection-block-meta">
          {/* written when the credentials are saved; no sync updates it */}
          Credentials saved: {new Date(connection.last_tested).toLocaleString()}
        </div>
      )}

      <div className="cn-action-bar">
        {apiType !== 'RAAS' && (
          <button className="cn-btn cn-btn-sync" onClick={handleSync} disabled={!!syncing || reconfiguring}>
            {syncing === 'running' ? '⟳ Pulling Data…' : syncing === 'done' ? '✓ Data Loaded' : '⟳ Pull Data'}
          </button>
        )}
        <button className="cn-btn cn-btn-test" onClick={() => setShowSyncSettings(!showSyncSettings)}>
          {showSyncSettings ? (apiType === 'RAAS' ? '▾ Hide Reports' : '▾ Hide Settings') : (apiType === 'RAAS' ? '▸ Browse Reports' : `▸ Sync Settings (${selectedCount}/${totalCount})`)}
        </button>
        <button className="cn-btn" onClick={() => { setReconfiguring(!reconfiguring); setTestResult(null) }}>
          {reconfiguring ? '✕ Cancel' : '✎ Reconfigure'}
        </button>
        <button className="cn-btn cn-btn-delete" onClick={handleDelete}>✕ Delete</button>
      </div>

      {reconfiguring && (
        <div className="cn-reconfig-form">
          <div className="cn-field-label" style={{marginBottom: 8}}>Update {apiType} Credentials <span style={{fontWeight:400, color:'var(--gray-400)', fontSize:12}}>— leave blank to keep existing</span></div>
          <div className="cn-cred-grid">
            {(CRED_FIELDS[apiType] || CRED_FIELDS.REST).map(f => (
              <div className="cn-cred-field" key={f.key}>
                <label className="cn-field-label">{f.label}</label>
                <input
                  className="cn-cred-input"
                  type={f.type}
                  name={`no-autofill-${f.key}`}
                  autoComplete={f.type === 'password' ? 'new-password' : 'off'}
                  placeholder={f.optional ? '(optional — saved)' : '••••••• (saved)'}
                  value={credValues[f.key] || ''}
                  onChange={e => setCredValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                />
                {f.hint && <div className="cn-cred-hint">{f.hint}</div>}
              </div>
            ))}
          </div>
          {testResult && (
            <div className={`cn-test-result ${testResult.success ? 'cn-test-ok' : 'cn-test-fail'}`}>
              {testResult.success ? '✓ ' : '✕ '}{testResult.message || testResult.error}
            </div>
          )}
          <div className="cn-reconfig-actions">
            <button className="cn-btn cn-btn-test" onClick={handleRetest} disabled={testing}>
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            <button className="cn-btn cn-btn-save" onClick={handleResave} disabled={saving}>
              {saving ? 'Saving…' : 'Save & Update'}
            </button>
          </div>
        </div>
      )}

      {showSyncSettings && apiType === 'RAAS' && (
        <div className="cn-sync-settings">
          <RaaSPanel customerId={customerId} />
        </div>
      )}

      {showSyncSettings && apiType !== 'RAAS' && (
        <div className="cn-sync-settings">
          <div className="cn-sync-settings-header">
            <span className="cn-field-label" style={{marginBottom: 0}}>
              {apiType} Endpoints ({selectedCount}/{totalCount})
            </span>
            <div style={{display:'flex', gap: 8}}>
              <button className="cn-link-btn" onClick={() => setSelectedEndpoints(
                Object.fromEntries(Object.keys(endpointDefs).map(k => [k, true]))
              )}>All</button>
              <button className="cn-link-btn" onClick={() => setSelectedEndpoints(
                Object.fromEntries(Object.entries(endpointDefs).map(([k, v]) => [k, v.default]))
              )}>Recommended</button>
              <button className="cn-link-btn" onClick={() => setSelectedEndpoints(
                Object.fromEntries(Object.keys(endpointDefs).map(k => [k, false]))
              )}>None</button>
            </div>
          </div>
          {catalogError && (
            <div className="cn-endpoint-error">
              Could not load the endpoint catalog from the backend ({catalogError}).
              Nothing is listed on purpose — showing a stale hand-maintained list here
              would let you tick endpoints that cannot sync. Retry, or check
              /api/connector/endpoints.
            </div>
          )}
          {!catalogError && Object.keys(endpointDefs).length === 0 && (
            <div className="cn-endpoint-error">Loading endpoints…</div>
          )}
          {areas.map(area => (
            <div key={area} className="cn-endpoint-group">
              <div className="cn-endpoint-area">{area}</div>
              {Object.entries(endpointDefs)
                .filter(([, def]) => def.area === area)
                .map(([name, def]) => {
                  const hasParams = !!ENDPOINT_PARAMS[name]
                  const isExpanded = expandedParam === name
                  const params = ENDPOINT_PARAMS[name] || []
                  const resolved = paramOptions[name] || {}
                  const currentVals = endpointParams[name] || {}

                  return (
                    <div key={name} className={`cn-endpoint-block ${isExpanded ? 'cn-expanded' : ''}`}>
                      <label className={`cn-endpoint-row ${def.heavy ? 'cn-heavy' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!selectedEndpoints[name]}
                          onChange={e => setSelectedEndpoints(prev => ({...prev, [name]: e.target.checked}))}
                        />
                        <span className="cn-endpoint-name">{def.label}</span>
                        {def.heavy && <span className="cn-heavy-badge">Large</span>}
                        {def.disabled_reason && (
                          <span
                            className="cn-endpoint-reason"
                            title={def.disabled_reason}
                          >
                            {String(def.disabled_reason).split(':')[0]}
                          </span>
                        )}
                        {def.table_name && def.table_name !== name && (
                          <span className="cn-endpoint-meta" title={`Writes table: ${def.table_name}`}>
                            → {def.table_name}
                          </span>
                        )}
                        {def.last_synced_at && (
                          <span className="cn-endpoint-meta" title={`Last synced: ${new Date(def.last_synced_at).toLocaleString()}`}>
                            {def.row_count != null ? `${def.row_count.toLocaleString()} rows` : '✓'} · {new Date(def.last_synced_at).toLocaleDateString()}
                          </span>
                        )}
                        {hasParams && (
                          <button
                            className={`cn-param-toggle ${isExpanded ? 'cn-param-active' : ''} ${Object.keys(currentVals).filter(k => currentVals[k]).length > 0 ? 'cn-param-set' : ''}`}
                            onClick={e => { e.preventDefault(); e.stopPropagation(); toggleParams(name) }}
                            title="Configure filters"
                          >
                            ⚙{Object.keys(currentVals).filter(k => currentVals[k]).length > 0 && (
                              <span className="cn-param-dot" />
                            )}
                          </button>
                        )}
                      </label>

                      {isExpanded && (
                        <div className="cn-param-panel">
                          {loadingParams === name ? (
                            <div className="cn-param-loading">Loading options…</div>
                          ) : (
                            params.map(p => (
                              <div key={p.name} className="cn-param-field">
                                <label className="cn-param-label">
                                  {p.label}
                                  {p.required && <span className="cn-param-req">*</span>}
                                </label>
                                {p.type === 'select' ? (
                                  <select
                                    className="cn-param-select"
                                    value={currentVals[p.name] || ''}
                                    onChange={e => setParamValue(name, p.name, e.target.value)}
                                  >
                                    <option value="">{p.required ? 'Select…' : 'All (no filter)'}</option>
                                    {(resolved[p.name]?.options || []).map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                    {(!resolved[p.name]?.options || resolved[p.name]?.options?.length === 0) && (
                                      <option disabled>No data — sync source endpoint first</option>
                                    )}
                                  </select>
                                ) : p.type === 'date' ? (
                                  <input
                                    type="date"
                                    className="cn-param-date"
                                    value={currentVals[p.name] || ''}
                                    onChange={e => setParamValue(name, p.name, e.target.value)}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="cn-param-text"
                                    value={currentVals[p.name] || ''}
                                    onChange={e => setParamValue(name, p.name, e.target.value)}
                                    placeholder={p.description || ''}
                                  />
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )
                })
              }
            </div>
          ))}
        </div>
      )}

      {syncStatus && syncing && (
        <div className="cn-sync-status">
          <div className="cn-sync-progress">
            <div className="cn-sync-bar">
              <div className="cn-sync-fill" style={{ width: `${syncStatus.total_tables > 0 ? (syncStatus.tables_processed / syncStatus.total_tables) * 100 : 0}%` }} />
            </div>
            <span className="cn-sync-label">{syncStatus.tables_processed}/{syncStatus.total_tables} tables · {(syncStatus.total_rows || 0).toLocaleString()} rows</span>
          </div>
          {syncStatus.current_step && <div className="cn-sync-step">{syncStatus.current_step}</div>}
          {syncStatus.errors?.length > 0 && (
            <div className="cn-sync-errors">
              {syncStatus.errors.map((e, i) => <div key={i} className="cn-sync-error">{e}</div>)}
            </div>
          )}
        </div>
      )}

      {/* ─── RaaS Reports Panel (SOAP connections only) ─── */}
      {apiType === 'SOAP' && isConnected && (
        <RaaSPanel customerId={customerId} />
      )}
    </div>
  )
}

/* ─── RaaS Reports Panel ─── */
function RaaSPanel({ customerId }) {
  const [step, setStep] = useState('idle')      // idle | config | loading | reports | executing | done | error
  const [companyFolder, setCompanyFolder] = useState('')
  const [raasConfig, setRaasConfig] = useState(null)
  const [reports, setReports] = useState([])
  const [selectedReport, setSelectedReport] = useState('')
  const [execResult, setExecResult] = useState(null)
  const [previewResult, setPreviewResult] = useState(null)
  const [errMsg, setErrMsg] = useState('')
  const [expanded, setExpanded] = useState(false)
  // Multi-select: pick reports, then pull them one at a time (each pull is its own
  // /raas/execute call, same as the single Pull button). Keyed by path -- report
  // names repeat across folders.
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState(() => new Set())
  const [batch, setBatch] = useState([])           // [{name, path, status, rows, table, error}]
  const [batchRunning, setBatchRunning] = useState(false)
  const [batchFinalize, setBatchFinalize] = useState('')  // '' | queuing | queued | failed: ...
  const stopBatch = useRef(false)

  const headers = {
    'Authorization': `Bearer ${localStorage.getItem('xlr8_token') || ''}`,
    'Content-Type': 'application/json',
  }
  // API is module-scoped above — the local copy here is why this component worked
  // while the endpoint fetch silently did not.

  // Check config on first expand
  async function checkConfig() {
    setStep('loading')
    setErrMsg('')
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/raas/config`, { headers })
      if (!res.ok) {
        // A settings read that failed (503) is not "no folders set up".
        const err = await res.json().catch(() => ({}))
        setErrMsg(err.detail || `RaaS settings could not be read (HTTP ${res.status})`)
        setStep('error')
        return
      }
      const data = await res.json()
      setRaasConfig(data)
      if (data.has_soap_credentials) {
        await loadReports()
      } else {
        setErrMsg('RaaS credentials not found. Please add a UKG_PRO_RAAS or UKG_PRO_SOAP connection with valid credentials.')
        setStep('error')
      }
    } catch (e) {
      setStep('config')
    }
  }

  async function loadReports() {
    setStep('loading')
    setErrMsg('')
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/raas/reports`, { headers })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setReports(data.reports || [])
      setStep('reports')
    } catch (e) {
      setErrMsg(e.message)
      setStep('error')
    }
  }

  async function saveConfig() {
    if (!companyFolder.trim()) return
    setStep('loading')
    try {
      await fetch(`${API}/api/customers/${customerId}/raas/config`, {
        method: 'POST', headers,
        body: JSON.stringify({ company_folder: companyFolder.trim() }),
      })
      await loadReports()
    } catch (e) {
      setErrMsg(e.message)
      setStep('error')
    }
  }

  async function previewReport(reportName, reportPath) {
    setSelectedReport(reportName)
    setStep('executing')
    setErrMsg('')
    setPreviewResult(null)
    setExecResult(null)
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/raas/execute`, {
        method: 'POST', headers,
        body: JSON.stringify({ report_name: reportName, report_path: reportPath, preview_only: true, save_to_duckdb: false }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setPreviewResult(data)
      setStep('preview')
    } catch (e) {
      setErrMsg(e.message)
      setStep('error')
    }
  }

  async function executeReport(reportName, reportPath) {
    setSelectedReport(reportName)
    setStep('executing')
    setErrMsg('')
    setExecResult(null)
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/raas/execute`, {
        method: 'POST', headers,
        body: JSON.stringify({ report_name: reportName, report_path: reportPath, save_to_duckdb: true }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setExecResult(data)
      setStep('done')
    } catch (e) {
      setErrMsg(e.message)
      setStep('error')
    }
  }

  function toggleSelected(path) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(path) ? next.delete(path) : next.add(path)
      return next
    })
  }

  // Pull the selected reports sequentially. A failure is recorded on its row and the
  // run moves on; Stop finishes the report in flight, then halts.
  async function runBatch() {
    const queue = reports.filter(r => selected.has(r.path))
    if (!queue.length) return
    stopBatch.current = false
    setBatchFinalize('')
    setBatchRunning(true)
    setBatch(queue.map(r => ({ name: r.name, path: r.path, status: 'queued' })))
    setStep('batch')
    const update = (i, patch) => setBatch(prev => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)))
    let loaded = 0

    for (let i = 0; i < queue.length; i++) {
      if (stopBatch.current) {
        setBatch(prev => prev.map((b, j) => (j >= i && b.status === 'queued' ? { ...b, status: 'skipped' } : b)))
        break
      }
      const r = queue[i]
      update(i, { status: 'running' })
      try {
        const res = await fetch(`${API}/api/customers/${customerId}/raas/execute`, {
          method: 'POST', headers,
          // finalize:false -- the post-ingest finalize runs ONCE after the batch, below.
          body: JSON.stringify({ report_name: r.name, report_path: r.path, save_to_duckdb: true, finalize: false }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
        const saveError = data.meta?.save_error
        if (!saveError && data.row_count > 0) loaded++
        update(i, {
          status: saveError ? 'failed' : 'done',
          rows: data.row_count,
          table: data.meta?.saved_table,
          error: saveError ? `Pulled ${data.row_count} rows but save failed: ${saveError}` : undefined,
        })
      } catch (e) {
        update(i, { status: 'failed', error: e.message })
      }
    }
    // One finalize for the whole batch (also after Stop), if anything landed.
    if (loaded > 0) {
      setBatchFinalize('queuing')
      try {
        const res = await fetch(`${API}/api/customers/${customerId}/raas/finalize`, { method: 'POST', headers })
        setBatchFinalize(res.ok ? 'queued' : `failed: HTTP ${res.status}`)
      } catch (e) {
        setBatchFinalize(`failed: ${e.message}`)
      }
    } else {
      setBatchFinalize('')
    }
    setBatchRunning(false)
  }

  function handleExpand() {
    setExpanded(!expanded)
    if (!expanded && step === 'idle') checkConfig()
  }

  return (
    <div className="cn-raas-panel">
      <div className="cn-raas-header" onClick={handleExpand}>
        <div className="cn-raas-title">
          <span className="cn-raas-icon">📡</span>
          <span>RaaS Reports</span>
          <span className="cn-raas-subtitle">Pull BI / Cognos reports via SOAP</span>
        </div>
        <span className="cn-raas-chevron">{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div className="cn-raas-body">

          {/* Loading */}
          {step === 'loading' && (
            <div className="cn-raas-loading">Loading…</div>
          )}

          {/* Configure company folder */}
          {step === 'config' && (
            <div className="cn-raas-config">
              <div className="cn-raas-config-text">
                Set the Cognos company folder for this customer's RaaS reports.
                <br /><span style={{ color: 'var(--gray-400)', fontSize: 11 }}>Path: Team content / __zzzCompany Folders / <strong>[Company Folder]</strong> / UltiPro / HCMPACT</span>
              </div>
              <div className="cn-raas-config-row">
                <input
                  className="cn-cred-input"
                  type="text"
                  placeholder="e.g. __Customer E Holdings, LLC"
                  value={companyFolder}
                  onChange={e => setCompanyFolder(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveConfig()}
                />
                <button className="cn-btn cn-btn-save" onClick={saveConfig} disabled={!companyFolder.trim()}>
                  Save & Load Reports
                </button>
              </div>
            </div>
          )}

          {/* Report list */}
          {step === 'reports' && (
            <div className="cn-raas-reports">
              {reports.length === 0 ? (
                <div className="cn-raas-empty">No reports found.</div>
              ) : (() => {
                const q = filter.trim().toLowerCase()
                const shown = q
                  ? reports.filter(r => r.name.toLowerCase().includes(q) || (r.folder || '').toLowerCase().includes(q))
                  : reports
                const allShownSelected = shown.length > 0 && shown.every(r => selected.has(r.path))
                const toggleAllShown = () => setSelected(prev => {
                  const next = new Set(prev)
                  shown.forEach(r => (allShownSelected ? next.delete(r.path) : next.add(r.path)))
                  return next
                })
                return (
                <>
                  <div className="cn-raas-report-header">
                    {reports.length} report{reports.length !== 1 ? 's' : ''} available
                    <button className="cn-link-btn" onClick={loadReports}>⟳ Refresh</button>
                  </div>
                  <div className="cn-raas-multi-bar">
                    <input
                      className="cn-cred-input"
                      type="text"
                      placeholder="Filter reports (e.g. SEMP-Rec)"
                      value={filter}
                      onChange={e => setFilter(e.target.value)}
                    />
                    <label className="cn-raas-select-all">
                      <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} disabled={!shown.length} />
                      Select all shown ({shown.length})
                    </label>
                    {selected.size > 0 && (
                      <button className="cn-link-btn" onClick={() => setSelected(new Set())}>Clear</button>
                    )}
                    <button className="cn-btn cn-btn-sync" onClick={runBatch} disabled={!selected.size}>
                      ⟳ Pull selected ({selected.size})
                    </button>
                  </div>
                  {[...new Set(shown.map(r => r.folder || 'Other'))].sort().map(folder => (
                    <div key={folder} className="cn-raas-folder-group">
                      <div className="cn-raas-folder-name">{folder} ({shown.filter(r => (r.folder || 'Other') === folder).length})</div>
                      {shown.filter(r => (r.folder || 'Other') === folder).map(r => (
                        <div key={r.path} className="cn-raas-report-row">
                          <label className="cn-raas-report-pick">
                            <input type="checkbox" checked={selected.has(r.path)} onChange={() => toggleSelected(r.path)} />
                            <span className="cn-raas-report-name">{r.name}</span>
                          </label>
                          <div style={{display:"flex",gap:6}}>
                            <button className="cn-btn" onClick={() => previewReport(r.name, r.path)} title="Preview first 20 rows without saving">👁 Preview</button>
                            <button className="cn-btn cn-btn-sync" onClick={() => executeReport(r.name, r.path)}>⟳ Pull</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
                )
              })()}
            </div>
          )}

          {/* Batch run: selected reports, one at a time */}
          {step === 'batch' && (
            <div className="cn-raas-done">
              <div className="cn-raas-done-header">
                {batchRunning ? <span className="cn-raas-exec-spinner" /> : <span className="cn-raas-done-icon">✓</span>}
                <strong>
                  {batch.filter(b => b.status === 'done').length} of {batch.length} pulled
                  {batch.some(b => b.status === 'failed') && ` · ${batch.filter(b => b.status === 'failed').length} failed`}
                  {batch.some(b => b.status === 'skipped') && ` · ${batch.filter(b => b.status === 'skipped').length} skipped`}
                </strong>
              </div>
              {batchFinalize && (
                <div className="cn-raas-done-note">
                  {batchFinalize === 'queued' && 'Post-load checks & observations queued once for the whole batch.'}
                  {batchFinalize === 'queuing' && 'Queuing post-load checks & observations…'}
                  {batchFinalize.startsWith('failed') && `Post-load checks were NOT queued (${batchFinalize}).`}
                </div>
              )}
              <div className="cn-raas-batch-list">
                {batch.map(b => (
                  <div key={b.path} className={`cn-raas-batch-row is-${b.status}`}>
                    <span className="cn-raas-batch-status">
                      {{ queued: '·', running: '⟳', done: '✓', failed: '✕', skipped: '–' }[b.status]}
                    </span>
                    <span className="cn-raas-report-name">{b.name}</span>
                    <span className="cn-raas-batch-detail">
                      {b.status === 'done' && <>{b.rows} rows{b.table && <> → <code>{b.table}</code></>}</>}
                      {b.status === 'failed' && b.error}
                      {b.status === 'running' && 'pulling…'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="cn-raas-done-actions">
                {batchRunning ? (
                  <button className="cn-btn" onClick={() => { stopBatch.current = true }}>
                    ■ Stop after current
                  </button>
                ) : (
                  <>
                    <button className="cn-btn cn-btn-sync" onClick={() => setStep('reports')}>← Back to Reports</button>
                    {batch.some(b => b.status === 'failed') && (
                      <button className="cn-btn" onClick={() => {
                        setSelected(new Set(batch.filter(b => b.status === 'failed').map(b => b.path)))
                        setStep('reports')
                      }}>Select failed to retry</button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Executing */}
          {step === 'executing' && (
            <div className="cn-raas-executing">
              <div className="cn-raas-exec-spinner" />
              <span>Pulling report: <strong>{selectedReport}</strong>…</span>
            </div>
          )}

          {/* Preview */}
          {step === 'preview' && previewResult && (
            <div className="cn-raas-done">
              <div className="cn-raas-done-header">
                <span className="cn-raas-done-icon">👁</span>
                <strong>{previewResult.report_name}</strong> — {previewResult.row_count} total rows · {previewResult.columns?.length} columns · preview only
              </div>
              <div className="cn-raas-done-columns" style={{marginBottom:8}}>
                <strong>Columns:</strong> {previewResult.columns?.join(', ')}
              </div>
              {previewResult.sample?.length > 0 && (
                <div style={{overflowX:'auto',fontSize:11,marginBottom:12}}>
                  <table style={{borderCollapse:'collapse',width:'100%'}}>
                    <thead>
                      <tr>{previewResult.columns?.map(c => <th key={c} style={{padding:'4px 8px',background:'var(--gray-100)',borderBottom:'1px solid var(--gray-200)',textAlign:'left',whiteSpace:'nowrap'}}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {previewResult.sample.map((row, i) => (
                        <tr key={i} style={{borderBottom:'1px solid var(--gray-100)'}}>
                          {previewResult.columns?.map(c => <td key={c} style={{padding:'4px 8px',whiteSpace:'nowrap',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis'}}>{String(row[c] ?? '')}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="cn-raas-done-actions">
                <button className="cn-btn cn-btn-sync" onClick={() => executeReport(previewResult.report_name, previewResult.report_path)}>⟳ Pull & Save to DuckDB</button>
                <button className="cn-btn" onClick={() => setStep('reports')}>← Back</button>
              </div>
            </div>
          )}

          {/* Done */}
          {step === 'done' && execResult && (
            <div className="cn-raas-done">
              <div className="cn-raas-done-header">
                <span className="cn-raas-done-icon">✓</span>
                <strong>{execResult.report_name}</strong> — {execResult.row_count} rows, {execResult.columns?.length} columns
              </div>
              {execResult.meta?.solution_matrix_updated && (
                <div className="cn-raas-done-note">⚡ Solution Matrix automatically updated with {execResult.meta.product_keys_found} product keys</div>
              )}
              {execResult.meta?.saved_table && (
                <div className="cn-raas-done-table">Saved to: <code>{execResult.meta.saved_table}</code></div>
              )}
              {execResult.columns && (
                <div className="cn-raas-done-columns">
                  Columns: {execResult.columns.join(', ')}
                </div>
              )}
              <div className="cn-raas-done-actions">
                <button className="cn-btn cn-btn-sync" onClick={() => setStep('reports')}>← Back to Reports</button>
                <button className="cn-btn" onClick={() => executeReport(execResult.report_name)}>⟳ Pull Again</button>
              </div>
            </div>
          )}

          {/* Error */}
          {step === 'error' && (
            <div className="cn-raas-error">
              <span className="cn-raas-error-icon">✕</span>
              <span>{errMsg || 'An error occurred'}</span>
              <button className="cn-link-btn" onClick={checkConfig}>Retry</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── SystemConnectionCard: one system, multiple connections ─── */
function SystemConnectionCard({ system, existingConnections, backendProducts, customerId, onSaved, vendors }) {
  const [expanded, setExpanded] = useState(false)
  const [showNewConnection, setShowNewConnection] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState('')
  const [creds, setCreds] = useState({})
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [saving, setSaving] = useState(false)

  const availableProducts = backendProducts || []
  const hasConnections = existingConnections.length > 0
  const connectedCount = existingConnections.filter(c => c.test_status === 'success').length

  // Which products don't have a connection yet
  const connectedProducts = new Set(existingConnections.map(c => c.product_id))
  const unconnectedProducts = availableProducts.filter(p => !connectedProducts.has(p))

  const apiType = getApiTypeFromProduct(selectedProduct)
  const credFields = CRED_FIELDS[apiType] || CRED_FIELDS.REST
  const badge = connectionBadge(system, { connectedCount, hasConnections }, vendors)
  const note = connectorNote(system, vendors)

  async function handleTest() {
    if (!selectedProduct) return
    setTesting(true); setTestResult(null)
    try {
      const result = await testConnection({
        vendor_id: (system.vendor_id || '').toUpperCase(),
        product_id: selectedProduct,
        ...creds,
      }, customerId)
      setTestResult(result)
    } catch (err) {
      setTestResult({ success: false, error: err.message })
    } finally { setTesting(false) }
  }

  async function handleSave() {
    if (!selectedProduct) return
    setSaving(true)
    try {
      const failure = saveFailure(await saveConnection(customerId, {
        vendor_id: (system.vendor_id || '').toUpperCase(),
        product_id: selectedProduct,
        ...creds,
      }))
      if (failure) {
        setTestResult({ success: false, error: failure })
        return
      }
      setTestResult({ success: true, message: 'Saved successfully' })
      setShowNewConnection(false)
      setCreds({})
      setSelectedProduct('')
      if (onSaved) onSaved()
    } catch (err) { alert('Save failed: ' + err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className={`cn-system-card ${connectedCount > 0 ? 'cn-connected' : ''}`}>
      <div className="cn-system-header" onClick={() => setExpanded(!expanded)}>
        <div className="cn-system-icon">{(system.vendor_id || '?').toUpperCase().slice(0, 3)}</div>
        <div className="cn-system-info">
          <div className="cn-system-name">{system.vendor_name || system.vendor_id} — {system.product_name || system.product_id}</div>
          <div className="cn-system-meta">
            {system.domain?.toUpperCase() || 'HCM'}
            {system.functions?.length > 0 && (' · ' + system.functions.join(', '))}
            {note && <span className="cn-provided-note">· {note}</span>}
            {system.is_primary && <span className="cn-primary-badge">Primary</span>}
          </div>
        </div>
        <div className="cn-system-status-area">
          <span className={`cn-status-badge cn-status-${badge.tone}`}>{badge.label}</span>
          <span className="cn-expand-icon">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>

      {expanded && (
        <div className="cn-system-body">
          {/* Each saved connection gets its own block */}
          {existingConnections.map((conn, i) => (
            <ConnectionBlock
              key={conn.product_id || i}
              connection={conn}
              system={system}
              customerId={customerId}
              onSaved={onSaved}
              onDelete={onSaved}
            />
          ))}

          {/* Add another connection */}
          {!showNewConnection && unconnectedProducts.length > 0 && (
            <button className="cn-add-connection-btn" onClick={() => {
              setShowNewConnection(true)
              setSelectedProduct(unconnectedProducts[0])
            }}>
              + Add {hasConnections ? 'Another' : ''} Connection ({unconnectedProducts.length} API type{unconnectedProducts.length > 1 ? 's' : ''} available)
            </button>
          )}

          {/* No connections yet — show setup prompt */}
          {!hasConnections && !showNewConnection && (
            <div className="cn-no-connections">
              {note
                ? <p>This product is {note}, so no direct connection is expected. Add one only if the customer has its own API access:</p>
                : <p>No connections configured. Choose an API type to get started:</p>}
              <div className="cn-api-type-grid">
                {availableProducts.map(p => (
                  <button key={p} className="cn-api-type-btn"
                    onClick={() => { setShowNewConnection(true); setSelectedProduct(p) }}>
                    <span className="cn-api-type-name">{getApiTypeFromProduct(p)}</span>
                    <span className="cn-api-type-id">{p}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* New connection form */}
          {showNewConnection && (
            <div className="cn-new-connection-form">
              <div className="cn-new-connection-header">
                <span className="cn-field-label" style={{ marginBottom: 0 }}>New Connection</span>
                <button className="cn-change" onClick={() => { setShowNewConnection(false); setTestResult(null) }}>Cancel</button>
              </div>

              <div className="cn-field-group">
                <label className="cn-field-label">API Type</label>
                <div className="cn-api-type-grid">
                  {unconnectedProducts.map(p => (
                    <button key={p} className={`cn-api-type-btn ${selectedProduct === p ? 'active' : ''}`}
                      onClick={() => { setSelectedProduct(p); setTestResult(null) }}>
                      <span className="cn-api-type-name">{getApiTypeFromProduct(p)}</span>
                      <span className="cn-api-type-id">{p}</span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedProduct && (
                <>
                  <div className="cn-field-group">
                    <label className="cn-field-label">Credentials — {apiType}</label>
                    <div className="cn-cred-grid">
                      {credFields.map(f => (
                        <div key={f.key} className={`cn-cred-field ${f.optional ? 'cn-optional' : ''}`}>
                          <label className="cn-cred-label">
                            {f.label}
                            {f.optional && <span className="cn-optional-tag">Optional</span>}
                          </label>
                          <input className="cn-cred-input" type={f.type} placeholder={f.placeholder}
                            name={`no-autofill-${f.key}`}
                            autoComplete={f.type === 'password' ? 'new-password' : 'off'}
                            value={creds[f.key] || ''}
                            onChange={e => setCreds(prev => ({ ...prev, [f.key]: e.target.value }))} />
                          {f.hint && <div className="cn-cred-hint">{f.hint}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="cn-action-bar">
                    <button className="cn-btn cn-btn-test" onClick={handleTest} disabled={testing}>
                      {testing ? '⟳ Testing…' : '🔌 Test Connection'}
                    </button>
                    <button className="cn-btn cn-btn-save" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : '💾 Save Connection'}
                    </button>
                  </div>

                  {testResult && (
                    <div className={`cn-test-result ${testResult.success !== false ? 'cn-result-ok' : 'cn-result-err'}`}>
                      <span className="cn-result-icon">{testResult.success !== false ? '✓' : '✗'}</span>
                      <div className="cn-result-body">
                        <div className="cn-result-msg">
                          {testResult.success !== false ? (testResult.message || 'Connection successful') : (testResult.error || 'Connection failed')}
                        </div>
                        {testResult.endpoints_available != null && <div className="cn-result-detail">{testResult.endpoints_available} endpoints available</div>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AddConnectionPanel({ vendors, customerId, onSaved, onClose }) {
  const [vendorSearch, setVendorSearch] = useState('')
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState('')
  const [creds, setCreds] = useState({})
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [saving, setSaving] = useState(false)

  const filtered = vendors.filter(v =>
    (v.vendor_id || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
    (v.vendor_name || '').toLowerCase().includes(vendorSearch.toLowerCase())
  )

  async function handleSelectVendor(v) {
    setSelectedVendor(v)
    setSelectedProduct('')
    setProducts(v.products || [])
  }

  const apiType = getApiTypeFromProduct(selectedProduct)
  const credFields = CRED_FIELDS[apiType] || CRED_FIELDS.REST

  async function handleTest() {
    if (!selectedProduct || !selectedVendor) return
    setTesting(true); setTestResult(null)
    try {
      const result = await testConnection({
        vendor_id: selectedVendor.vendor_id,
        product_id: selectedProduct,
        ...creds,
      })
      setTestResult(result)
    } catch (err) {
      setTestResult({ success: false, error: err.message })
    } finally { setTesting(false) }
  }

  async function handleSave() {
    if (!selectedProduct || !selectedVendor) return
    setSaving(true)
    try {
      const failure = saveFailure(await saveConnection(customerId, {
        vendor_id: selectedVendor.vendor_id,
        product_id: selectedProduct,
        ...creds,
      }))
      if (failure) {
        setTestResult({ success: false, error: failure })
        return
      }
      if (onSaved) onSaved()
      if (onClose) onClose()
    } catch (err) { alert('Save failed: ' + err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="cn-add-panel">
      <div className="cn-add-header">
        <h3>Add Manual Connection</h3>
        <button className="cn-change" onClick={onClose}>Close</button>
      </div>
      <div className="cn-add-body">
        <input className="cn-search" placeholder="Search 84+ vendors…" value={vendorSearch}
          onChange={e => setVendorSearch(e.target.value)} />
        {!selectedVendor && (
          <div className="cn-vendor-list" style={{maxHeight: 320, overflowY: "auto"}}>
            {filtered.map(v => (
              <div key={v.vendor_id} className="cn-vendor-item" onClick={() => handleSelectVendor(v)}>
                <span className="cn-vendor-name">{v.vendor_name || v.vendor_id}</span>
                <span className="cn-vendor-count">{(v.products || []).length} products</span>
              </div>
            ))}
          </div>
        )}
        {selectedVendor && (
          <>
            <div style={{ marginBottom: 12 }}>
              <button className="cn-change" onClick={() => { setSelectedVendor(null); setSelectedProduct('') }}>
                ← Change vendor
              </button>
              <span style={{ marginLeft: 8, fontWeight: 600 }}>{selectedVendor.vendor_name || selectedVendor.vendor_id}</span>
            </div>
            <div className="cn-api-type-grid">
              {products.map(p => (
                <button key={p} className={`cn-api-type-btn ${selectedProduct === p ? 'active' : ''}`}
                  onClick={() => setSelectedProduct(p)}>
                  <span className="cn-api-type-name">{getApiTypeFromProduct(p)}</span>
                  <span className="cn-api-type-id">{p}</span>
                </button>
              ))}
            </div>
            {selectedProduct && (
              <>
                <div className="cn-field-group">
                  <label className="cn-field-label">Credentials — {apiType}</label>
                  <div className="cn-cred-grid">
                    {credFields.map(f => (
                      <div key={f.key} className={`cn-cred-field ${f.optional ? 'cn-optional' : ''}`}>
                        <label className="cn-cred-label">{f.label}</label>
                        <input className="cn-cred-input" type={f.type} placeholder={f.placeholder}
                          name={`no-autofill-${f.key}`}
                          autoComplete={f.type === 'password' ? 'new-password' : 'off'}
                          value={creds[f.key] || ''} onChange={e => setCreds(prev => ({ ...prev, [f.key]: e.target.value }))} />
                        {f.hint && <div className="cn-cred-hint">{f.hint}</div>}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="cn-action-bar">
                  <button className="cn-btn cn-btn-test" onClick={handleTest} disabled={testing}>
                    {testing ? '⟳ Testing…' : '🔌 Test'}
                  </button>
                  <button className="cn-btn cn-btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : '💾 Save'}
                  </button>
                </div>
                {testResult && (
                  <div className={`cn-test-result ${testResult.success !== false ? 'cn-result-ok' : 'cn-result-err'}`}>
                    <span className="cn-result-icon">{testResult.success !== false ? '✓' : '✗'}</span>
                    <span className="cn-result-msg">{testResult.success !== false ? testResult.message : testResult.error}</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default function Connectors({ customerId: propCustomerId } = {}) {
  const params = useParams()
  const customerId = propCustomerId || params.customerId
  const embedded = !!propCustomerId

  const [customer, setCustomer] = useState(null)
  const [connections, setConnections] = useState([])
  const [vendors, setVendors] = useState([])
  const [platformConfig, setPlatformConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [showAdd, setShowAdd] = useState(false)

  // Any of the four failing is a failed load, shown with a Retry: an unread connection
  // list must never read as "no connections".
  async function loadAll() {
    try {
      const [cust, conns, vends, pconf] = await Promise.all([
        getCustomer(customerId),
        getConnections(customerId),
        getConnectorVendors(),
        getPlatformConfig(),
      ])
      setCustomer(cust)
      setConnections(listFrom(conns))
      setVendors(vends || [])
      setPlatformConfig(pconf)
      setLoadError(null)
    } catch (err) {
      console.error('Load error:', err)
      setLoadError(err)
    }
    finally { setLoading(false) }
  }
  useEffect(() => { loadAll() }, [customerId])

  const systems = customer?.systems || []
  const companyName = customer?.company_name || customerId

  // Every saved connection is shown: under the system whose product family holds it, or
  // in its own list when it matches no system (see utils/connectionGroups.js).
  const { bySystem, unplaced } = groupConnections(systems, connections, platformConfig)

  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Connectors' },
  ]

  if (loading) {
    if (embedded) return <div className="cn-loading"><div className="cn-spinner" /><p>Loading…</p></div>
    return (<><TopNav breadcrumbs={breadcrumbs} /><div className="cn-loading"><div className="cn-spinner" /><p>Loading…</p></div></>)
  }

  const inner = (
    <div className={embedded ? 'cn-embedded' : 'cn-page'}>
      {loadError && <LoadError what="the connections" error={loadError} onRetry={loadAll} />}
      {embedded ? (
        <div className="cn-embedded-header">
          <span className="cn-embedded-title">API Connectors</span>
          <span className="cn-embedded-meta">{systems.length} system{systems.length !== 1 ? 's' : ''} · {connections.length} active connection{connections.length !== 1 ? 's' : ''}</span>
          <button className="cn-btn cn-btn-sync" style={{marginLeft:'auto'}} onClick={() => setShowAdd(true)}>+ Add Connection</button>
        </div>
      ) : (
        <div className="cn-header">
          <h1 className="cn-title">API Connectors</h1>
          <p className="cn-subtitle">{companyName} · {systems.length} system{systems.length !== 1 ? 's' : ''} configured · {connections.length} active connection{connections.length !== 1 ? 's' : ''}</p>
        </div>
      )}

      {systems.length > 0 && (
        <div className="cn-section">
          {!embedded && <h2 className="cn-section-title">Customer Systems</h2>}
          {!embedded && <p className="cn-section-desc">These systems are from {companyName}'s profile. Expand to set up API credentials and pull data.</p>}
          <div className="cn-system-list">
            {bySystem.map(({ system: s, backendProducts, connections: mine }, i) => (
              <SystemConnectionCard key={s.id || i} system={s} existingConnections={mine}
                backendProducts={backendProducts} customerId={customerId} onSaved={loadAll}
                vendors={platformConfig?.vendors} />
            ))}
          </div>
        </div>
      )}

      {unplaced.length > 0 && (
        <div className="cn-section">
          <h2 className="cn-section-title">Connections not matched to a system</h2>
          <p className="cn-section-desc">
            These connections are saved, but no system in {companyName}'s profile is for their
            product. Add the system to the profile to show them under it.
          </p>
          <div className="cn-system-list">
            {unplaced.map((conn, i) => (
              <div key={`${conn.vendor_id || '?'}-${conn.product_id || i}`} className="cn-system-card">
                <div className="cn-system-body">
                  <ConnectionBlock
                    connection={conn}
                    system={{ vendor_id: conn.vendor_id || '' }}
                    customerId={customerId}
                    onSaved={loadAll}
                    onDelete={loadAll}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}



      {showAdd && (
        <div className="cn-section">
          <AddConnectionPanel vendors={vendors} customerId={customerId} onSaved={loadAll} onClose={() => setShowAdd(false)} />
        </div>
      )}

      {!embedded && <DataTablesSection customerId={customerId} />}
    </div>
  )

  if (embedded) return inner
  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={
        <button className="nav-btn primary" onClick={() => setShowAdd(true)}>+ Add Connection</button>
      } />
      {inner}
    </>
  )
}

/* ─── DataTablesSection: list and manage synced tables ─── */
function DataTablesSection({ customerId }) {
  const [tables, setTables] = useState([])
  const [expanded, setExpanded] = useState(false)
  const [deleting, setDeleting] = useState(null)

  async function loadTables() {
    try {
      const t = await getConnectorTables(customerId)
      setTables(Array.isArray(t) ? t : [])
    } catch (e) { console.error(e) }
  }

  useEffect(() => { if (expanded) loadTables() }, [expanded, customerId])

  async function handleDelete(tableName) {
    if (!confirm(`Delete table "${tableName}"? This removes the data from DuckDB and metadata from Supabase. Its findings are kept; open ones are dismissed as "table removed".`)) return
    setDeleting(tableName)
    try {
      await deleteTable(customerId, tableName)
      setTables(prev => prev.filter(t => (t.table_name || t) !== tableName))
    } catch (e) { alert('Delete failed: ' + e.message) }
    setDeleting(null)
  }

  return (
    <div className="cn-section">
      <h2 className="cn-section-title cn-clickable" onClick={() => setExpanded(!expanded)} style={{cursor:'pointer'}}>
        {expanded ? '▾' : '▸'} Data Tables {tables.length > 0 && `(${tables.length})`}
      </h2>
      {expanded && (
        <div className="cn-tables-list">
          {tables.length === 0 && <p style={{color:'var(--gray-400)', fontSize: 13}}>No tables loaded yet. Sync an API connection to pull data.</p>}
          {tables.map(t => {
            const name = t.table_name || t
            const rows = t.row_count || t.rows || '?'
            const area = t.functional_area || ''
            const productId = t.product_id || ''
            const apiType = productId.toUpperCase().includes('SOAP') ? 'SOAP' : productId.toUpperCase().includes('REST') ? 'REST' : t.source_type || ''
            const syncDate = t.last_synced_at || t.updated_at || ''
            const dateStr = syncDate ? new Date(syncDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
            return (
              <div key={name} className="cn-table-row">
                <div className="cn-table-info">
                  <div className="cn-table-name">{name}</div>
                  <div className="cn-table-meta">
                    {rows.toLocaleString?.()} rows{area && ` · ${area}`}
                    {apiType && <span className={`cn-table-badge ${apiType.toLowerCase()}`}>{apiType}</span>}
                    {dateStr && ` · ${dateStr}`}
                  </div>
                </div>
                <button
                  className="cn-btn cn-btn-delete-sm"
                  onClick={() => handleDelete(name)}
                  disabled={deleting === name}
                  title="Delete table"
                >
                  {deleting === name ? '…' : '✕'}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}


